import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Course, UserEnrollment } from '../../models/interfaces/course.interface';
import { CourseType } from '../../models/enums/course-type.enum';
import { CourseLocation } from '../../models/enums/course-location.enum';
import { CoursesByCategory } from '../../models/interfaces/courses-by-category.interface';
import { Api } from '../api/api';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { getCrudErrors } from '../../utils/functions';
import { ModuleService } from '../module/module.service';
import { courseMock } from '../../mocks/interfaces/course.mock';
import { userCourseMock } from '../../mocks/interfaces/user-course.mock';
import { categoryMock } from '../../mocks/interfaces/category.mock';
import { applyMockCrudParams } from '../../utils/mock-crud.util';
import { appendCourseFilterParams, matchesCourseFacets } from '../../utils/course-filter.util';
import { UserCourse } from '../../models/interfaces/user-course.interface';
import { RecommendedCourseProxy } from '../../models/interfaces/recommended-course.interface';

export interface CourseFilterParams {
  categoryIds?: string[];
  type?: CourseType | null;
  location?: CourseLocation | null;
  searchTerm?: string;
  limit?: number;
}

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  private readonly api: Api = new Api();
  private readonly moduleService: ModuleService = new ModuleService();

  private courses: Course[] = structuredClone(courseMock);
  private enrollments: UserCourse[] = structuredClone(userCourseMock);
  private categories = structuredClone(categoryMock);

  //#region Real API

  public async getMyEnrollments(): Promise<UserEnrollment[]> {
    if (environment.useFakeBackend) {
      return this.getMyEnrollmentsMocked();
    }

    const { error, success } = await this.api.getAsync<UserEnrollment[]>(environment.routes.courses.me);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  public async getRecommended(): Promise<Course[]> {
    if (environment.useFakeBackend) {
      return this.getRecommendedMocked();
    }

    const { error, success } = await this.api.getAsync<RecommendedCourseProxy[]>(
      environment.routes.courses.recommendations,
    );

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    const courses = success
      .map((recommendation) => recommendation.course)
      .filter((course): course is Course => !!course);

    return this.enrichCoursesMetadata(courses);
  }

  public getCoursesByGroupId(groupId: string): Promise<Course[]> {
    if (environment.useFakeBackend) {
      return this.getCoursesByGroupIdMocked(groupId);
    }

    return this.search({
      search: {
        trackIds: { $in: [groupId] },
      },
    });
  }

  public async getCoursesGroupedByCategory(filters: CourseFilterParams = {}): Promise<CoursesByCategory[]> {
    if (environment.useFakeBackend) {
      return this.getCoursesGroupedByCategoryMocked(filters);
    }

    const params = new URLSearchParams();
    appendCourseFilterParams(params, filters);

    const query = params.toString();
    const url = query
      ? `${ environment.routes.courses.groupedByCategory }?${ query }`
      : environment.routes.courses.groupedByCategory;

    const { error, success } = await this.api.getAsync<CoursesByCategory[]>(url);

    if (!success) {
      throw new Error(getCrudErrors(error)[0]);
    }

    const allCourses = success.flatMap((group) => group.courses ?? []);
    const enrichedCourses = await this.enrichCoursesMetadata(allCourses);
    const enrichedById = new Map(enrichedCourses.map((course) => [course.id, course]));

    return success.map((group) => ({
      ...group,
      courses: (group.courses ?? []).map((course) => enrichedById.get(course.id) ?? course),
    }));
  }

  public async getAll(): Promise<Course[]> {
    if (environment.useFakeBackend) {
      return this.getAllMocked();
    }

    const url = environment.routes.courses.base;
    const { error, success } = await this.api.getAsync<Course[]>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return this.enrichCoursesMetadata(success);
  }

  public async search(params: CrudRequestParams<Course>): Promise<Course[]> {
    if (environment.useFakeBackend) {
      return this.searchMocked(params);
    }

    const url = createCrudUrl<Course>(environment.routes.courses.search, params);
    const { error, success } = await this.api.getAsync<PaginationDto<Course>>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return this.enrichCoursesMetadata(success.data);
  }

  public async getById(courseId: string, params?: CrudRequestParams<Course>): Promise<Course | undefined> {
    if (environment.useFakeBackend) {
      return this.getByIdMocked(courseId);
    }

    const url = createCrudUrl<Course>(environment.routes.courses.one(courseId), params);
    const { error, success } = await this.api.getAsync<Course>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  public async enrichCoursesMetadata(courses: Course[]): Promise<Course[]> {
    return this.enrichCoursesMetadataInternal(courses);
  }

  //#endregion

  //#region Fake Backend

  public async getMyEnrollmentsMocked(): Promise<UserEnrollment[]> {
    return this.enrollments.map(enrollment => ({
      id: enrollment.id,
      tenantId: enrollment.course?.tenantId ?? 'tenant-uuid',
      userId: enrollment.userId,
      courseId: enrollment.courseId,
      progress: enrollment.progress,
      startedAt: enrollment.startedAt,
      completedAt: enrollment.completedAt,
      lastAccessedAt: enrollment.lastAccessedAt,
      course: enrollment.course ?? this.courses.find(course => course.id === enrollment.courseId)!,
    }));
  }

  public getRecommendedMocked(): Promise<Course[]> {
    return this.getAllMocked();
  }

  public getCoursesByGroupIdMocked(groupId: string): Promise<Course[]> {
    return this.searchMocked({
      search: {
        trackIds: { $in: [groupId] },
      },
    });
  }

  public async getCoursesGroupedByCategoryMocked(filters: CourseFilterParams = {}): Promise<CoursesByCategory[]> {
    const filteredCourses = this.filterCourses(this.courses, filters);
    const enrichedCourses = await this.enrichCoursesMetadataMocked(filteredCourses);
    const categoryIds = filters.categoryIds?.length
      ? filters.categoryIds
      : this.categories.map(category => category.id);

    return categoryIds
      .map(categoryId => {
        const category = this.categories.find(item => item.id === categoryId);

        return {
          categoryId,
          categoryName: category?.name ?? categoryId,
          courses: enrichedCourses.filter(course =>
            (course.categoryId ?? course.category?.id) === categoryId),
        };
      })
      .filter(group => group.courses.length > 0);
  }

  public async getAllMocked(): Promise<Course[]> {
    return this.enrichCoursesMetadataMocked([...this.courses]);
  }

  public async searchMocked(params: CrudRequestParams<Course> = {}): Promise<Course[]> {
    const filtered = applyMockCrudParams(this.courses, params);
    return this.enrichCoursesMetadataMocked(filtered);
  }

  public async getByIdMocked(courseId: string): Promise<Course | undefined> {
    const course = this.courses.find(item => item.id === courseId);

    if (!course) {
      return undefined;
    }

    const [enriched] = await this.enrichCoursesMetadataMocked([course]);
    return enriched;
  }

  public async enrichCoursesMetadataMocked(courses: Course[]): Promise<Course[]> {
    return this.enrichCoursesMetadataInternal(courses);
  }

  public resetMockData(): void {
    this.courses = structuredClone(courseMock);
    this.enrollments = structuredClone(userCourseMock);
    this.categories = structuredClone(categoryMock);
  }

  //#endregion

  private filterCourses(courses: Course[], filters: CourseFilterParams): Course[] {
    return courses.filter(course => matchesCourseFacets(course, filters));
  }

  private async enrichCoursesMetadataInternal(courses: Course[]): Promise<Course[]> {
    if (!courses.length) {
      return [];
    }

    const courseIds = Array.from(new Set(courses.map((course) => course.id).filter(Boolean)));

    if (!courseIds.length) {
      return courses;
    }

    const modules = await this.moduleService.search({
      search: { courseId: { $in: courseIds } },
      sort: [{ field: 'order', order: 'ASC' }],
    });

    const moduleIds = modules.map((module) => module.id);
    const classes = moduleIds.length
      ? await this.moduleService.getClassesByModuleIds(moduleIds)
      : [];

    const moduleIdsByCourseId = modules.reduce<Map<string, string[]>>((accumulator, module) => {
      const courseModuleIds = accumulator.get(module.courseId) ?? [];
      courseModuleIds.push(module.id);
      accumulator.set(module.courseId, courseModuleIds);
      return accumulator;
    }, new Map<string, string[]>());

    const durationByCourseId = classes.reduce<Map<string, number>>((accumulator, moduleClass) => {
      const current = accumulator.get(moduleClass.courseId) ?? 0;
      accumulator.set(moduleClass.courseId, current + (moduleClass.duration ?? 0));
      return accumulator;
    }, new Map<string, number>());

    return courses.map((course) => ({
      ...course,
      modulesIds: (course.modulesIds?.length ?? 0) > 0
        ? course.modulesIds
        : moduleIdsByCourseId.get(course.id) ?? [],
      durationMinutes: (course.durationMinutes ?? 0) > 0
        ? course.durationMinutes
        : Math.round(durationByCourseId.get(course.id) ?? 0),
    }));
  }

}
